import type { AlmightyReferenceItem } from '~/utils/almightyReference'
import type { AlmightyPromptMentionToken } from '~/utils/almightyMention'

/**
 * 移动端 `/chat` 底部 MobileInputPanel 与创作页之间的状态桥接。
 *
 * 离开面板跳转时 `handleNavigateToCreate` 会写入 `mobileInputPanel_pending`；
 * 用户在创作页修改模型/比例后若未写回，返回聊天页仍会读到旧快照。
 * 各创作页在 **onBeforeRouteLeave**（优先，避免与 chat 侧读 session 竞态）及
 * onDeactivated / onUnmounted 时调用本方法覆盖为最新表单状态。
 *
 * `credit*` 可选字段与 `/api/.../workflow/credits/calculate/v1` 参数对齐，供底部栏展示与创作页一致的积分。
 */
export type MobileInputPanelCreditFields = {
    /** 文生图 / 图生图：分辨率 r1 */
    r1?: string
    /** 出图张数或图生图批量时的 n（与创作页一致） */
    n?: number
    /** 视频时长（秒） */
    t?: number | null
    /** 视频分辨率 r（如 720p） */
    r?: string
    /** 是否生成音频 */
    ga?: boolean
    /** 文生图 / 图生图：standard | batch | single */
    generateType?: string
    /** 批量对比时的模型列表，逗号拼接 */
    comparisionModelListCsv?: string
    /** 图生视频等：与 useWorkbenchCredits 一致的 workflow 变体 */
    videoWorkflow?:
        | 'imageToVideo'
        | 'multiImgToVideo'
        | 'firstLastImgToVideo'
        | 'almightyReferenceToVideo'
}

export type MobileInputPanelPendingPayload = {
    currentType: string
    prompt: string
    uploadedImage: string
    selectedModel: string
    currentRatio: string
    /** 图生视频：single_frame | multiple_frames 等，与创作页一致 */
    image_type?: string
    frame_end_mode?: boolean
    /** Seedance 全能参考素材与 @ 提示词分片（与创作页一致） */
    almighty_reference_items?: AlmightyReferenceItem[]
    almighty_prompt_tokens?: AlmightyPromptMentionToken[]
} & MobileInputPanelCreditFields

export function writeMobileInputPanelPendingFromCreationPage(
    payload: MobileInputPanelPendingPayload
) {
    if (typeof window === 'undefined') return
    try {
        sessionStorage.setItem(
            'mobileInputPanel_pending',
            JSON.stringify({
                visible: true,
                currentType: payload.currentType,
                prompt: payload.prompt ?? '',
                uploadedImage: payload.uploadedImage ?? '',
                selectedModel: payload.selectedModel ?? '',
                currentRatio: payload.currentRatio ?? '',
                r1: payload.r1,
                n: payload.n,
                t: payload.t,
                r: payload.r,
                ga: payload.ga,
                generateType: payload.generateType,
                comparisionModelListCsv: payload.comparisionModelListCsv,
                videoWorkflow: payload.videoWorkflow,
                image_type: payload.image_type,
                frame_end_mode: payload.frame_end_mode,
                almighty_reference_items: payload.almighty_reference_items,
                almighty_prompt_tokens: payload.almighty_prompt_tokens,
            })
        )
    } catch {
        // ignore quota / private mode
    }
}
